import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

//const api = axios.create({
//  baseURL: '/api',
//});
const api = axios.create({
  baseURL: 'https://a3s-labs-production.up.railway.app/api',
  timeout: 10000, // 10 seconds timeout
  //headers: {
  //  'Content-Type': 'application/json'
  //}
});

// Utility to check if token is expired or will expire soon (within 5 minutes)
function isTokenExpiredOrExpiringSoon(token: string): boolean {
  try {
    const decoded: any = jwtDecode(token);
    if (!decoded.exp) return false;
    // Check if token expires within the next 5 minutes
    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return decoded.exp * 1000 <= fiveMinutesFromNow;
  } catch {
    return true;
  }
}

// Utility to check if token is completely expired
function isTokenExpired(token: string): boolean {
  try {
    const decoded: any = jwtDecode(token);
    if (!decoded.exp) return false;
    return Date.now() >= decoded.exp * 1000;
  } catch {
    return true;
  }
}

// Utility to get auth headers
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

// Function to refresh token
const refreshToken = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await axios.post(`${api.defaults.baseURL}/refresh`, null, {
      params: { refresh_token: refreshToken }
    });
    
          const { access_token, refresh_token: newRefreshToken, user: userData } = response.data as {
        access_token: string;
        refresh_token?: string;
        user?: any;
      };
    
    localStorage.setItem('token', access_token);
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
    
    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

// Add a request interceptor to check token expiration before every request
api.interceptors.request.use((config) => {
  console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`);
  
  const token = localStorage.getItem('token');
  
  // Skip token check for login and refresh endpoints
  if (config.url === '/login' || config.url === '/refresh') {
    console.log('⏭️ Skipping token check for auth endpoints');
    return config;
  }

  if (token) {
    console.log('🔍 Checking token before request...');
    // Check if token is expired or will expire soon
    if (isTokenExpiredOrExpiringSoon(token)) {
      console.log('🔄 Token expiring soon, refreshing before request...');
      // Try to refresh the token
      refreshToken().then((refreshSuccess) => {
        if (!refreshSuccess) {
          console.log('❌ Token refresh failed, dispatching session expired event');
          // If refresh fails, dispatch session expired event
          window.dispatchEvent(new Event('session-expired'));
        } else {
          console.log('✅ Token refreshed successfully before request');
        }
      });
    } else {
      console.log('✅ Token is valid, proceeding with request');
    }
    
    // Add the token to the request
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
    console.log('🔑 Authorization header added to request');
  } else {
    console.log('⚠️ No token found for request');
  }
  
  return config;
});

// Add a response interceptor to handle 401 errors
api.interceptors.response.use(
  response => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async error => {
    console.log(`❌ API Error: ${error.response?.status || 'Network'} ${error.config?.url}`);
    
    const originalRequest = error.config;
    
    // If we get a 401 and this isn't a retry attempt
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔄 401 Unauthorized, attempting token refresh...');
      originalRequest._retry = true;
      
      // Skip retry for login and refresh endpoints
      if (originalRequest.url === '/login' || originalRequest.url === '/refresh') {
        console.log('⏭️ Skipping retry for auth endpoints, dispatching session expired');
        window.dispatchEvent(new Event('session-expired'));
        return Promise.reject(error);
      }
      
      // Try to refresh the token
      console.log('🔄 Attempting token refresh after 401...');
      const refreshSuccess = await refreshToken();
      if (refreshSuccess) {
        console.log('✅ Token refresh successful, retrying original request');
        // Retry the original request with the new token
        const newToken = localStorage.getItem('token');
        if (newToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } else {
        console.log('❌ Token refresh failed after 401, dispatching session expired');
        // If refresh fails, dispatch session expired event
        window.dispatchEvent(new Event('session-expired'));
      }
    }
    
    return Promise.reject(error);
  }
);

export { getAuthHeaders };
export default api;

export const getClassLevels = () => api.get('/subjects/class-levels');
export const getStudents = (school_id: number, class_level?: string) => api.get('/students/', { params: { school_id, ...(class_level ? { class_level } : {}) } });
export const getEnrolledStudents = (course_id: number, class_level?: string) => api.get(`/courses/${course_id}/students`, { params: { ...(class_level ? { class_level } : {}) } });
export const getUnenrolledStudents = (course_id: number, class_level?: string) => api.get(`/courses/${course_id}/unenrolled_students`, { params: { ...(class_level ? { class_level } : {}) } });
export const enrollStudent = (subject_id: number, course_id: number) => api.post('/courses/enroll', { subject_id, course_id });
export const unenrollStudent = (subject_id: number, course_id: number) => api.post('/courses/unenroll', { subject_id, course_id });

// Class Level Management API
export const getClassLevelsManagement = () => api.get('/class-levels');
export const getClassLevelStats = (classLevelKey: string) => api.get(`/class-levels/${classLevelKey}/stats`);
export const getStudentsByClassLevel = (classLevelKey: string) => api.get(`/class-levels/${classLevelKey}/students`);
export const getCoursesByClassLevel = (classLevelKey: string) => api.get(`/class-levels/${classLevelKey}/courses`);
export const bulkAssignStudentsToClassLevel = (classLevelKey: string, studentIds: number[]) => api.post(`/class-levels/${classLevelKey}/bulk-assign-students`, { student_ids: studentIds });

export const requestPasswordReset = (email: string, frontendUrl?: string) =>
  api.post('/password-reset/request', frontendUrl ? { email, frontend_url: frontendUrl } : { email });
export const confirmPasswordReset = (token: string, newPassword: string) => api.post('/password-reset/confirm', { token, new_password: newPassword });
